import { FilterKeyEnum } from '@hapvida/hapvida-core-components/src/constants/searchParams';

import { FEATURE_TOGGLE } from '@constants';
import { HapvidaApi } from '@http';

import type {
  CompaniesFilterListProps,
  CompaniesFilterListRequestDTO,
  CompaniesFilterListResponseDTO,
} from './dtos/CompaniesFilterListDTO';
import type {
  CompaniesListProps,
  CompaniesListRequestDTO,
  CompaniesListResponseDTO,
} from './dtos/CompaniesListDTO';
import type {
  BeneficiaryNumbersRequestDTO,
  BeneficiaryNumbersResponseDTO,
  BeneficiaryNumbersSummaryProps,
} from './dtos/CompanyBeneficiariesDTO';
import type {
  CompanyDetailRequestDTO,
  CompanyDetailResponseDTO,
} from './dtos/CompanyDetailDTO';
import type {
  CompanySummaryDetailRequestDTO,
  CompanySummaryDetailResponseDTO,
} from './dtos/CompanySummaryDetailDTO';
import type { RemoveIsNewCompanyStatusDTO } from './dtos/RemoveIsNewCompanyStatusDTO';
import type {
  AccessResponsibilityLetterRequestDTO,
  AccessResponsibilityLetterResponseDTO,
  ResponsibilityLetterRequestDTO,
  ResponsibilityLetterResponseDTO,
  AcceptResponsibilityLetterRequestDTO,
  HistoryResponsibilityLetterRequestDTO,
  HistoryResponsibilityLetterResponseDTO,
  CurrentCompanyResponseDTO,
} from './dtos';

export * from './dtos';

export default class CompanyService {
  private readonly endpoints = {
    companyDetail: (id: string) => `/api/v1/companies/${id}/company-details`,
    list: (id: string) => `/api/v1/companies/${id}/relates`,
    filterList: (id: string) => `/api/v1/companies/${id}/filter`,
    beneficiaries: (companyId: string) =>
      `/api/${
        FEATURE_TOGGLE.BENEFICIARIES_V3 ? 'v3' : 'v1'
      }/companies/${companyId}/beneficiaries/summary`,
    removeIsNewCompanyStatus: (id: string) =>
      `/api/v1/users/company/${id}/update-new-company-flag`,
    companySummaryDetail: (id: string) => `/api/v1/companies/${id}`,
    currentCompany: () => `/api/v3/companies/current`,
    accessResponsibilityLetter: (partnerCompany: string) =>
      `/api/v3/companies/${partnerCompany}/access-responsibility-letter`,
    responsibilityLetter: (partnerCompany: string) =>
      `/api/v3/companies/${partnerCompany}/responsibility-letter`,
    historyResponsibilityLetter: (partnerCompany: string) =>
      `/api/v3/companies/${partnerCompany}/history-responsibility-letter`,
  };

  public async fetchCompaniesList({
    id,
    ...rest
  }: CompaniesListRequestDTO): Promise<CompaniesListProps> {
    const { data } = await HapvidaApi.get<CompaniesListResponseDTO>(
      this.endpoints.list(id),
      {
        params: rest,
      },
    );

    return data.content;
  }

  public async fetchCompaniesFilterList({
    id,
    ...rest
  }: CompaniesFilterListRequestDTO): Promise<CompaniesFilterListProps> {
    const { data } = await HapvidaApi.get<CompaniesFilterListResponseDTO>(
      this.endpoints.filterList(id),
      {
        params: rest,
      },
    );

    return data.content;
  }

  public async fetchCompanyDetail({
    id,
  }: CompanyDetailRequestDTO): Promise<CompanyDetailResponseDTO['content']> {
    const {
      data: { content },
    } = await HapvidaApi.get<CompanyDetailResponseDTO>(
      this.endpoints.companyDetail(id),
    );

    return content;
  }

  public async fetchCompanySummaryDetail({
    id,
  }: CompanySummaryDetailRequestDTO): Promise<
    CompanySummaryDetailResponseDTO['content']
  > {
    const {
      data: { content },
    } = await HapvidaApi.get<CompanySummaryDetailResponseDTO>(
      this.endpoints.companySummaryDetail(id),
    );

    return content;
  }

  public async fetchCompanyBeneficiaryNumbers({
    companyId,
    afiliateId,
  }: BeneficiaryNumbersRequestDTO): Promise<BeneficiaryNumbersSummaryProps> {
    const { data } = await HapvidaApi.get<BeneficiaryNumbersResponseDTO>(
      this.endpoints.beneficiaries(companyId),
      {
        params: {
          [FilterKeyEnum.ORGANIZATIONAL_GROUP]: afiliateId,
        },
      },
    );

    return data.content;
  }

  public async removeIsNewCompanyStatus({ id }: RemoveIsNewCompanyStatusDTO) {
    const { data } = await HapvidaApi.put(
      this.endpoints.removeIsNewCompanyStatus(id),
    );

    return data;
  }

  public async getCurrentCompany() {
    const { data } = await HapvidaApi.get<CurrentCompanyResponseDTO>(
      this.endpoints.currentCompany(),
    );

    return data.content;
  }

  public async accessResponsibilityLetter({
    partnerCompany,
  }: AccessResponsibilityLetterRequestDTO) {
    const { data } =
      await HapvidaApi.get<AccessResponsibilityLetterResponseDTO>(
        this.endpoints.accessResponsibilityLetter(partnerCompany),
      );

    return data.content;
  }

  public async responsibilityLetter({
    partnerCompany,
  }: ResponsibilityLetterRequestDTO) {
    const { data } = await HapvidaApi.get<ResponsibilityLetterResponseDTO>(
      this.endpoints.responsibilityLetter(partnerCompany),
    );

    return data.content;
  }

  public async acceptResponsibilityLetter({
    partnerCompany,
    responsibilityLetterStatus,
  }: AcceptResponsibilityLetterRequestDTO) {
    await HapvidaApi.patch(
      this.endpoints.responsibilityLetter(partnerCompany),
      {
        responsibilityLetterStatus,
      },
    );
  }

  public async historyResponsibilityLetter({
    partnerCompany,
  }: HistoryResponsibilityLetterRequestDTO) {
    const { data } =
      await HapvidaApi.get<HistoryResponsibilityLetterResponseDTO>(
        this.endpoints.historyResponsibilityLetter(partnerCompany),
      );

    return data.content;
  }
}
